import { useMemo, useState } from "react";
import { DataTable, DataTableExpandedRows, DataTableRowEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import {
	AdminImportCourse,
	AdminImportResult,
	AdminImportStudent,
	AdminImportTeacher,
} from "../rosterImportApiSlice";
import { stepProgress } from "../rosterImportUtils";
import { PhaseIssuesPanel } from "./PhaseIssuesPanel";
import { ImportEntity, ImportRow, PhaseKey } from "./importSummaryUtils";

interface ImportSummaryTableRows {
	importCoursesResults: AdminImportResult;
	importTeachersResults: AdminImportResult;
	importStudentsResults: AdminImportResult;
}

interface ImportSummaryTableProps extends ImportSummaryTableRows {
	coursesToImport: AdminImportCourse[];
	teachersToImport: AdminImportTeacher[];
	studentsToImport: AdminImportStudent[];
}

export default function RosterImportSummaryTable({
	importCoursesResults,
	importTeachersResults,
	importStudentsResults,
	coursesToImport,
	teachersToImport,
	studentsToImport,
}: ImportSummaryTableProps) {
	const rows = useMemo<ImportRow[]>(() => {
		const makeRow = (key: PhaseKey, phase: string, result: AdminImportResult): ImportRow => {
			const errorMessages = result?.errorMessages ?? [];
			return {
				key,
				phase,
				inserts: result?.inserts ?? 0,
				updates: result?.updates ?? 0,
				noChanges: result?.noChanges ?? 0,
				issues: errorMessages.length,
				errorMessages,
			};
		};

		return [
			makeRow("courses", stepProgress.importCourses.phase, importCoursesResults),
			makeRow("teachers", stepProgress.importTeachers.phase, importTeachersResults),
			makeRow("students", stepProgress.importStudents.phase, importStudentsResults),
		];
	}, [importCoursesResults, importTeachersResults, importStudentsResults]);

	// Find the first row with errors and expand it by default
	const initialExpandedRow = useMemo(() => {
		const firstRowWithErrors = rows.find((row) => row.errorMessages.length > 0);
		return firstRowWithErrors ? { [firstRowWithErrors.key]: true } : {};
	}, [rows]);

	const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>(initialExpandedRow);

	const allowExpansion = (row: ImportRow) => row.errorMessages.length > 0;

	const onRowClick = (event: DataTableRowEvent) => {
		const row = event.data as ImportRow;
		//prevent rows with no errors from being expanded when clicked
		if (!allowExpansion(row)) return;

		const xRows: any = expandedRows;

		if (xRows && xRows[row.key]) {
			onRowCollapse(event);
		} else {
			onRowExpand(event);
		}
	};

	const onRowExpand = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);

			const tr = target.closest("tr");
			tr && tr?.classList.add("expanded");
		}
		setExpandedRows({ [event.data.key]: true });
	};

	const onRowCollapse = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);
		}
		setExpandedRows({});
	};

	//used for matching information in the PhaseIssuesPanel by their row number
	const entitiesByRowForPhase = useMemo(() => {
		const coursesByRow = new Map(
			(coursesToImport ?? []).map((c: AdminImportCourse) => [c.row, c as ImportEntity])
		);
		const teachersByRow = new Map(
			(teachersToImport ?? []).map((t: AdminImportTeacher) => [t.row, t as ImportEntity])
		);
		const studentsByRow = new Map(
			(studentsToImport ?? []).map((s: AdminImportStudent) => [s.row, s as ImportEntity])
		);

		return {
			courses: coursesByRow,
			teachers: teachersByRow,
			students: studentsByRow,
		};
	}, [coursesToImport, teachersToImport, studentsToImport]);

	const rowExpansionTemplate = (row: ImportRow) => (
		<PhaseIssuesPanel
			phaseKey={row.key}
			phaseTitle={row.phase}
			errorMessages={row.errorMessages}
			entitiesByRow={entitiesByRowForPhase[row.key]}
		/>
	);

	const phaseBodyTemplate = (row: ImportRow) => {
		const noErrors = row.errorMessages.length === 0;
		const iconClasses = `size-5 ${noErrors ? "text-green-700" : "text-red-500"}`;

		return (
			<span className="flex items-center gap-2">
				{noErrors ? (
					<CheckCircleIcon className={iconClasses} />
				) : (
					<ExclamationTriangleIcon className={iconClasses} />
				)}
				<span>{row.phase}</span>
			</span>
		);
	};

	return (
		<DataTable
			dataKey="key"
			value={rows}
			emptyMessage="No import results found"
			stripedRows
			className="tbl tbl-data tbl-datatable min-w-[40rem]"
			expandedRows={expandedRows}
			onRowClick={onRowClick}
			onRowExpand={onRowExpand}
			onRowCollapse={onRowCollapse}
			rowExpansionTemplate={rowExpansionTemplate}
			rowClassName={(row: ImportRow) =>
				`${allowExpansion(row) ? "cursor-pointer" : "cursor-default"}`
			}>
			<Column expander={allowExpansion} />
			<Column
				header="Phase"
				field="phase"
				body={phaseBodyTemplate}
				headerClassName="!px-2"
				className="!px-2"
			/>
			<Column header="New" field="inserts" headerClassName="!px-2" className="!px-2" />
			<Column header="Updates" field="updates" headerClassName="!px-2" className="!px-2" />
			<Column
				header="No Changes"
				field="noChanges"
				headerClassName="!px-2"
				className="!px-2"
			/>
			<Column header="Issues" field="issues" headerClassName="!px-2" className="!px-2" />
		</DataTable>
	);
}
